/* Playo shared UI: channel-ready badge (+ "Make channel-ready" action) and
   the pickers. LibraryPicker opens the CMS's own Media Library (the same
   modal used everywhere else, with upload), so logos, filler videos and
   schedule content all come from one place. */

import React, { useCallback, useEffect, useRef, useState } from "react";
import um from "../userManagement/userManagement.module.css";
import s from "./Playo.module.css";
import MediaLibraryModal from "../../component/mediaLibrary/MediaLibraryModal";
import { getChannelReady, makeChannelReady, searchAssets, apiErrorMessage } from "./playoService";

// Common zones are always offered: some ICU builds only list legacy
// canonical names (e.g. "Asia/Calcutta" instead of "Asia/Kolkata").
const COMMON_TIMEZONES = ["UTC", "Europe/London", "Europe/Berlin", "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles", "America/Sao_Paulo", "Asia/Kolkata", "Asia/Dubai", "Asia/Singapore", "Asia/Tokyo", "Australia/Sydney", "Africa/Lagos", "Africa/Johannesburg"];
export const TIMEZONES = (() => {
  let all = [];
  try {
    if (typeof Intl.supportedValuesOf === "function") all = Intl.supportedValuesOf("timeZone");
  } catch {
    /* old browser */
  }
  const rest = [...new Set(all)].filter((tz) => !COMMON_TIMEZONES.includes(tz)).sort();
  return [...COMMON_TIMEZONES, ...rest];
})();

/** Status → { label, tone } for a channel-ready state object (or a bare boolean). */
export function readyLabel(state) {
  if (state === true) return { label: "Channel-ready", tone: "ok" };
  if (state?.channel_ready) {
    // A re-run of a ready asset: it stays on air with its current output.
    if (state.rerun_status === "pending") return { label: "Channel-ready · re-run queued", tone: "info" };
    if (state.rerun_status === "processing") return { label: "Channel-ready · re-running…", tone: "info" };
    if (state.rerun_status === "failed") return { label: "Channel-ready · re-run failed", tone: "warn" };
    return { label: "Channel-ready", tone: "ok" };
  }
  if (state === false) return { label: "Not channel-ready", tone: "warn" };
  if (!state) return { label: "Unknown", tone: "muted" };
  if (state.stale) return { label: "Outdated ladder", tone: "warn" };
  switch (state.status) {
    case "pending":
      return { label: "Queued", tone: "info" };
    case "processing":
      return { label: "Transcoding…", tone: "info" };
    case "failed":
      return { label: "Transcode failed", tone: "bad" };
    default:
      return { label: "Not channel-ready", tone: "warn" };
  }
}

export function ReadyPill({ state, title }) {
  const { label, tone } = readyLabel(state);
  return (
    <span className={`${s.pill} ${s[`pill_${tone}`]}`} title={title || state?.error || label} data-testid="ready-pill">
      {label}
    </span>
  );
}

/** Loads one asset's channel-ready status; `refresh()` re-reads it. */
export function useChannelReady(videoId) {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(false);
  const refresh = useCallback(async () => {
    if (!videoId) {
      setState(null);
      return null;
    }
    setLoading(true);
    try {
      const st = await getChannelReady(videoId);
      setState(st || null);
      return st || null;
    } catch {
      setState(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [videoId]);
  useEffect(() => {
    refresh();
  }, [refresh]);
  return { state, loading, refresh, setState };
}

/** Badge + "Make channel-ready" for one asset. */
export function ChannelReadyStatus({ videoId, canEdit, onChange }) {
  const { state, loading, refresh, setState } = useChannelReady(videoId);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  if (!videoId) return null;
  const ready = Boolean(state?.channel_ready);
  const inFlight = state && (["pending", "processing"].includes(state.status) || (ready && ["pending", "processing"].includes(state.rerun_status)));
  const failed = state?.status === "failed" || (ready && state?.rerun_status === "failed");

  const request = async () => {
    setBusy(true);
    setError("");
    try {
      const res = await makeChannelReady(videoId);
      setState(res || null); // the API returns the asset's channel-ready status object
      onChange?.(res);
    } catch (e) {
      setError(apiErrorMessage(e, "Could not queue the transcode"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <span className={s.readyRow}>
      {loading && !state ? <span className={s.muted}>Checking…</span> : <ReadyPill state={state} />}
      {!ready && canEdit && !inFlight && (
        <button type="button" className={um.linkBtn} onClick={request} disabled={busy}>
          {busy ? "Queuing…" : state?.status === "failed" ? "Retry transcode" : "Make channel-ready"}
        </button>
      )}
      {inFlight && (
        <button type="button" className={um.linkBtn} onClick={refresh}>
          Refresh
        </button>
      )}
      {error && <span className={um.errorText}>{error}</span>}
      {failed && state.error && (
        <span className={um.errorText} data-testid="transcode-error">
          Reason: {state.error}
        </span>
      )}
    </span>
  );
}

/** Modal to pick a video from the media library. onPick({ video_id, name }). */
export function AssetPicker({ open, onClose, onPick, title = "Choose an asset" }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const seq = useRef(0);

  useEffect(() => {
    if (!open) return undefined;
    const mine = ++seq.current;
    const t = setTimeout(async () => {
      setLoading(true);
      setError("");
      try {
        const list = await searchAssets(q);
        if (mine === seq.current) setRows(list);
      } catch (e) {
        if (mine === seq.current) setError(apiErrorMessage(e, "Could not load the media library"));
      } finally {
        if (mine === seq.current) setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [q, open]);

  if (!open) return null;
  return (
    <div className={um.dialogOverlay} role="dialog" aria-modal="true" aria-label={title} onClick={onClose}>
      <div className={`${um.dialog} ${s.pickerDialog}`} onClick={(e) => e.stopPropagation()}>
        <h3 className={um.dialogTitle}>{title}</h3>
        <input
          className={um.input}
          placeholder="Search the media library…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          autoFocus
          aria-label="Search assets"
        />
        <div className={s.pickerList}>
          {loading && <div className={s.muted}>Loading…</div>}
          {error && <div className={um.errorText}>{error}</div>}
          {!loading && !error && rows.length === 0 && <div className={s.muted}>No assets found</div>}
          {rows.map((r) => (
            <button
              type="button"
              key={r.video_id}
              className={s.pickerRow}
              onClick={() => {
                onPick(r);
                onClose();
              }}
            >
              {r.image ? <img src={r.image} alt="" className={s.pickerThumb} /> : <span className={s.pickerThumb} />}
              <span className={s.pickerName}>{r.name}</span>
              <span className={s.muted}>
                {r.type} · #{r.video_id}
              </span>
            </button>
          ))}
        </div>
        <div className={um.dialogActions}>
          <button type="button" className={um.btnGhost} onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * The CMS Media Library, opened for Playo.
 *  - kind "image":   pick one image (e.g. a channel logo) → onPick(item); item.bunny_cdn_url is its URL.
 *  - kind "video":   pick one uploaded video (e.g. the filler) → onPick(item); item.id is the Video id.
 *  - kind "content": pick many movies / clips / whole series → onPickMany(items) on Done.
 * Uploading new images/videos works inside the library as usual.
 */
export function LibraryPicker({ open, kind, onClose, onPick = () => {}, onPickMany = () => {} }) {
  if (!open) return null;
  const setIsOpen = (next) => {
    if (!next) onClose();
  };
  if (kind === "content") {
    return (
      <MediaLibraryModal
        isOpen
        setIsOpen={setIsOpen}
        mode="modal"
        showCheckbox
        selectMultipal
        sectionManager="sectionManager"
        preSelected={[]}
        onMultiVideoSelect={(items) => onPickMany(items || [])}
      />
    );
  }
  const pickOne = (item) => {
    if (!item) return;
    onPick(item);
    onClose();
  };
  return (
    <MediaLibraryModal
      isOpen
      setIsOpen={setIsOpen}
      mode="modal"
      showCheckbox
      type={kind}
      {...(kind === "image" ? { handleimagesvalue: pickOne } : { handleVideovalue: pickOne })}
    />
  );
}
